// Generates realistic mock data for each month of the fiscal year (Apr 2025 - Mar 2026)
import realCloseTasks from './closeTasks';
import { GL_MONTHS, getCompanyGLSections, getCompanyGLHistory, getCompanyBudget } from './companyGL';

const glSectionsFn = getCompanyGLSections();

// Map app month key (e.g. '2026-02') to GL_MONTHS index (0-13)
function getGLMonthIdx(monthKey) {
  const idx = GL_MONTHS.indexOf(monthKey);
  return idx >= 0 ? idx : GL_MONTHS.length - 1; // fallback to latest
}

// Dynamic: always include from Apr 2025 through current month + 1
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS = (() => {
  const startYear = 2025, startMonth = 4; // Apr 2025
  const now = new Date();
  const endYear = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
  const endMonth = (now.getMonth() + 2) % 12 || 12; // current + 1 (1-indexed)
  const endYearAdj = now.getMonth() >= 11 ? endYear : now.getFullYear();
  const result = [];
  let y = startYear, m = startMonth;
  while (y < endYearAdj || (y === endYearAdj && m <= endMonth)) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    result.push({ key, label: `${MONTH_NAMES[m - 1]} ${y}`, short: MONTH_SHORT[m - 1] });
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return result;
})();

export { MONTHS };

// Seed-based pseudo-random for consistent data per month
function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function hashMonth(key) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Revenue grows ~3% per month from a base
const BASE_PRODUCT_REV = 128000;
const BASE_SERVICE_REV = 28000;

function getGLForMonth(monthIdx) {
  const growth = 1 + monthIdx * 0.03;
  const rand = seededRandom(hashMonth(MONTHS[monthIdx].key));

  const productRev = Math.round(BASE_PRODUCT_REV * growth * (0.97 + rand() * 0.06));
  const serviceRev = Math.round(BASE_SERVICE_REV * (1 + monthIdx * 0.005) * (0.98 + rand() * 0.04));
  const subs = Math.round((22000 + monthIdx * 1500) * (0.9 + rand() * 0.2));
  const hosting = Math.round((5200 + monthIdx * 150) * (0.95 + rand() * 0.1));
  const salaries = monthIdx < 3 ? 72000 : 74200;
  const rent = 8200;
  const software = Math.round((3800 + monthIdx * 300) * (0.85 + rand() * 0.3));
  const travel = Math.round((800 + rand() * 2000) * (0.5 + rand()));
  const depreciation = 3180;

  return {
    revenue: [
      { name: 'Product revenue', code: '4000', prior: Math.round(productRev * 0.96), current: productRev },
      { name: 'Services revenue', code: '4100', prior: Math.round(serviceRev * 0.99), current: serviceRev },
    ],
    cogs: [
      { name: 'Subcontractors', code: '5200', prior: Math.round(subs * 0.92), current: subs },
      { name: 'Hosting & infrastructure', code: '5300', prior: Math.round(hosting * 0.97), current: hosting },
    ],
    opex: [
      { name: 'Salaries & wages', code: '6100', prior: salaries, current: salaries },
      { name: 'Rent & occupancy', code: '6400', prior: rent, current: rent },
      { name: 'Software & subscriptions', code: '6510', prior: Math.round(software * 0.9), current: software },
      { name: 'Travel & entertainment', code: '6600', prior: Math.round(travel * 1.2), current: travel },
      { name: 'Depreciation', code: '7100', prior: depreciation, current: depreciation },
    ],
    bs: [
      { name: 'SR&ED receivable', code: '1216', prior: Math.round(42000 + monthIdx * 300), current: Math.round(42000 + (monthIdx + 1) * 300) },
      { name: 'Prepaid expenses', code: '1300', prior: Math.round(18600 - monthIdx * 250), current: Math.round(18600 - (monthIdx + 1) * 250) },
      { name: 'Fixed assets (net)', code: '1500', prior: Math.round(63600 - monthIdx * 3180), current: Math.round(63600 - (monthIdx + 1) * 3180) },
    ],
  };
}

function getCloseTasksForMonth(monthIdx) {
  const m = MONTHS[monthIdx];
  const [year, mon] = m.key.split('-').map(Number);
  const dueBase = (day) => `${year}-${String(mon + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  // For past months (before Feb 2026 = index 10), all tasks are done
  // For current month (Feb 2026), use mixed status
  // For future months, nothing done
  const isPast = monthIdx < 10;
  const isCurrent = monthIdx === 10;

  const rand = seededRandom(hashMonth(m.key) + 42);
  const ts = (day, h, min) => `${year}-${String(mon + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`;

  const taskDefs = [
    { name: 'Download bank statements', section: 'Bank & Cash', day: 1, accounts: '11010-11110' },
    { name: 'Bank reconciliation \u2014 Bank A operating', section: 'Bank & Cash', day: 3, workbook: 'Bank Rec', accounts: '11010' },
    { name: 'Bank reconciliation \u2014 Bank A payroll', section: 'Bank & Cash', day: 2, workbook: 'Bank Rec', accounts: '11020' },
    { name: 'Post payroll accrual', section: 'Accruals & Adjustments', day: 1, accounts: '61010' },
    { name: 'Amortization \u2014 prepaid expenses', section: 'Accruals & Adjustments', day: 4, workbook: 'Prepaids', accounts: '14100' },
    { name: 'Fixed asset depreciation (15th-day)', section: 'Accruals & Adjustments', day: 2, workbook: 'FA Schedule', accounts: '15010-15055' },
    { name: 'SR&ED receivable \u2014 1216/1210', section: 'Accruals & Adjustments', day: 3, workbook: 'SR&ED Receivable', accounts: '12600' },
    { name: 'Trial balance review', section: 'Review & Sign-off', day: 3, workbook: 'Trial Balance', accounts: 'All' },
    { name: 'P&L variance review vs prior month', section: 'Review & Sign-off', day: 5, accounts: 'P&L' },
    { name: 'Balance sheet flux commentary', section: 'Review & Sign-off', day: 5, accounts: 'BS' },
    { name: 'CFO package export', section: 'Review & Sign-off', day: 6, accounts: 'All' },
    { name: 'Lock period in Sage Intacct', section: 'Review & Sign-off', day: 4, accounts: 'All' },
  ];

  // For current month (Feb 2026), specific tasks are done
  const currentDoneIds = [1, 3, 4, 6, 7, 8, 12];
  const currentReviewedIds = [1, 3, 4, 7, 8];

  return taskDefs.map((def, i) => {
    const id = i + 1;
    let done, reviewed, completedBy, completedAt, reviewedBy, reviewedAt;

    if (isPast) {
      done = true;
      reviewed = true;
      completedBy = 'AM';
      completedAt = ts(def.day, 9 + Math.floor(rand() * 8), Math.floor(rand() * 60));
      reviewedBy = 'SR';
      reviewedAt = ts(Math.min(def.day + 1, 28), 9 + Math.floor(rand() * 8), Math.floor(rand() * 60));
    } else if (isCurrent) {
      done = currentDoneIds.includes(id);
      reviewed = currentReviewedIds.includes(id);
      completedBy = done ? 'AM' : null;
      completedAt = done ? ts(def.day, 9 + Math.floor(rand() * 8), Math.floor(rand() * 60)) : null;
      reviewedBy = reviewed ? 'SR' : null;
      reviewedAt = reviewed ? ts(Math.min(def.day + 1, 28), 9 + Math.floor(rand() * 8), Math.floor(rand() * 60)) : null;
    } else {
      done = false;
      reviewed = false;
      completedBy = null;
      completedAt = null;
      reviewedBy = null;
      reviewedAt = null;
    }

    return {
      id,
      name: def.name,
      section: def.section,
      assignedTo: 'AM',
      reviewer: 'SR',
      dueDate: dueBase(def.day),
      done,
      completedBy,
      completedAt,
      reviewed,
      reviewedBy,
      reviewedAt,
      documents: id <= 3 ? [`Bank A ${id === 1 ? 'Statements' : id === 2 ? 'Operating' : 'Payroll'} - ${m.label}.pdf`] : [],
      workbook: def.workbook || null,
      accounts: def.accounts || null,
      blocked: false,
    };
  });
}

function getBudgetForMonth(monthIdx) {
  const rand = seededRandom(hashMonth(MONTHS[monthIdx].key) + 100);
  const gl = getGLForMonth(monthIdx);
  const totalRev = gl.revenue.reduce((s, a) => s + a.current, 0);

  return [
    { category: 'Revenue', budget: Math.round(totalRev * (0.95 + rand() * 0.05)), actual: totalRev },
    { category: 'Subcontractors', budget: Math.round(gl.cogs[0].current * (0.8 + rand() * 0.15)), actual: gl.cogs[0].current },
    { category: 'Salaries', budget: gl.opex[0].current, actual: gl.opex[0].current },
    { category: 'Rent', budget: 8200, actual: 8200 },
    { category: 'Software', budget: Math.round(gl.opex[2].current * (0.75 + rand() * 0.2)), actual: gl.opex[2].current },
    { category: 'Travel', budget: Math.round(2000 * (0.8 + rand() * 0.4)), actual: gl.opex[3].current },
    { category: 'Hosting', budget: Math.round(gl.cogs[1].current * (0.9 + rand() * 0.1)), actual: gl.cogs[1].current },
  ];
}

function getCashFlowForMonth(monthIdx) {
  // Return trailing 6 months of cash flow up to and including this month
  const result = [];
  for (let i = Math.max(0, monthIdx - 5); i <= monthIdx; i++) {
    const gl = getGLForMonth(i);
    const inflow = gl.revenue.reduce((s, a) => s + a.current, 0);
    const outflow = [...gl.cogs, ...gl.opex].flat().reduce((s, a) => s + (a.current || 0), 0);
    result.push({ month: MONTHS[i].short, inflow, outflow });
  }
  return result;
}

function getRevenueExpenseTrend(monthIdx) {
  const result = { months: [], revenue: [], expenses: [] };
  for (let i = Math.max(0, monthIdx - 5); i <= monthIdx; i++) {
    const gl = getGLForMonth(i);
    result.months.push(MONTHS[i].short);
    result.revenue.push(gl.revenue.reduce((s, a) => s + a.current, 0));
    result.expenses.push(
      [...gl.cogs, ...gl.opex].flat().reduce((s, a) => s + (a.current || 0), 0)
    );
  }
  return result;
}

function getGLAccountsStructured(monthIdx) {
  const gl = getGLForMonth(monthIdx);
  return [
    { section: 'Revenue', accounts: gl.revenue.map(a => ({ name: a.name, code: a.code, jan: a.prior, feb: a.current })) },
    { section: 'Cost of Goods Sold', accounts: gl.cogs.map(a => ({ name: a.name, code: a.code, jan: a.prior, feb: a.current })) },
    { section: 'Operating Expenses', accounts: gl.opex.map(a => ({ name: a.name, code: a.code, jan: a.prior, feb: a.current })) },
    { section: 'Balance Sheet', accounts: gl.bs.map(a => ({ name: a.name, code: a.code, jan: a.prior, feb: a.current })) },
  ];
}

function getGLHistory(monthIdx) {
  // Build 12-month history ending at this month
  const history = {};
  const accountCodes = ['4000', '4100', '5200', '5300', '6100', '6400', '6510', '6600', '7100'];
  const accountNames = {
    '4000': 'Product revenue', '4100': 'Services revenue',
    '5200': 'Subcontractors', '5300': 'Hosting & infra',
    '6100': 'Salaries & wages', '6400': 'Rent & occupancy',
    '6510': 'Software & subs', '6600': 'Travel & entertainment',
    '7100': 'Depreciation',
  };

  for (const code of accountCodes) {
    history[code] = { code, name: accountNames[code], monthly: [] };
  }

  for (let i = Math.max(0, monthIdx - 11); i <= monthIdx; i++) {
    const gl = getGLForMonth(i);
    const allAccounts = [...gl.revenue, ...gl.cogs, ...gl.opex];
    for (const a of allAccounts) {
      if (history[a.code]) {
        history[a.code].monthly.push(a.current);
      }
    }
  }

  return Object.values(history);
}

export function getMonthData(monthKey) {
  const idx = MONTHS.findIndex(m => m.key === monthKey);
  if (idx === -1) return null;

  const month = MONTHS[idx];
  const priorMonth = idx > 0 ? MONTHS[idx - 1] : null;

  // Use the demo GL dataset (mock data)
  // Maps app month to the 14-month GL dataset (Jan 2025 – Feb 2026)
  const glIdx = getGLMonthIdx(monthKey);
  const hasRealGL = glIdx >= 0;

  return {
    month,
    priorMonth,
    closeTasks: idx === 10 ? realCloseTasks.map(t => ({ ...t })) : getCloseTasksForMonth(idx),
    glAccounts: hasRealGL ? glSectionsFn(glIdx) : getGLAccountsStructured(idx),
    glHistory: hasRealGL ? getCompanyGLHistory(glIdx) : getGLHistory(idx),
    budget: hasRealGL ? getCompanyBudget() : getBudgetForMonth(idx),
    cashFlow: getCashFlowForMonth(idx),
    trend: getRevenueExpenseTrend(idx),
  };
}
